import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { IAppState } from '@app/app.state';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { ToastService } from 'ngx-mt-ui-lib/services/toast';
import { Dialog } from '@angular/cdk/dialog';
import { PvsTaskActions } from './pvs-task.actions';
import { PvsTaskApiService } from '@app/core/api/pvs-task-api.service';
import { catchError, map, of, switchMap, tap } from 'rxjs';

@Injectable()
export class PvsTaskEffects {
  protected readonly dialog = inject(Dialog);

  private readonly store = inject(Store<IAppState>);
  private readonly toastService = inject(ToastService);
  private readonly translateService = inject(TranslateService);
  private readonly actions$ = inject(Actions);
  private readonly router = inject(Router);

  private readonly pvsTaskApiService = inject(PvsTaskApiService);

  readonly requestTaskInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PvsTaskActions.requestTaskInfo),
      switchMap(() =>
        this.pvsTaskApiService.getPvsTaskInfo().pipe(
          map((task) =>
            PvsTaskActions.requestTaskInfoSuccess({
              task,
            }),
          ),
          catchError((error) =>
            of(PvsTaskActions.requestTaskInfoError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestSetTaskAccept$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PvsTaskActions.requestSetTaskAccept),
      switchMap((action) =>
        this.pvsTaskApiService.setPvsTaskAccept(action.id).pipe(
          map(() => PvsTaskActions.requestSetTaskAcceptSuccess()),
          catchError((error) =>
            of(PvsTaskActions.requestSetTaskAcceptError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestSetTaskOnWay$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PvsTaskActions.requestSetTaskOnWay),
      switchMap((action) =>
        this.pvsTaskApiService.setPvsTaskOnWay(action.id).pipe(
          map(() => PvsTaskActions.requestSetTaskOnWaySuccess()),
          catchError((error) =>
            of(PvsTaskActions.requestSetTaskOnWayError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestSetTaskAtObject$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PvsTaskActions.requestSetTaskAtObject),
      switchMap((action) =>
        this.pvsTaskApiService.setPvsTaskAtObject(action.id).pipe(
          map(() => PvsTaskActions.requestSetTaskAtObjectSuccess()),
          catchError((error) =>
            of(PvsTaskActions.requestSetTaskAtObjectError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestSetTaskInProgress$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PvsTaskActions.requestSetTaskInProgress),
      switchMap((action) =>
        this.pvsTaskApiService.setPvsTaskInProgress(action.id).pipe(
          map(() => PvsTaskActions.requestSetTaskInProgressSuccess()),
          catchError((error) =>
            of(PvsTaskActions.requestSetTaskInProgressError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestSetTaskComplete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PvsTaskActions.requestSetTaskComplete),
      switchMap((action) =>
        this.pvsTaskApiService.setPvsTaskComplete(action.id).pipe(
          map(() => PvsTaskActions.requestSetTaskCompleteSuccess()),
          catchError((error) =>
            of(PvsTaskActions.requestSetTaskCompleteError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly requestSetTaskCancel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(PvsTaskActions.requestSetTaskCancel),
      switchMap((action) =>
        this.pvsTaskApiService.setPvsTaskCancel(action.id).pipe(
          map(() => PvsTaskActions.requestSetTaskCancelSuccess()),
          catchError((error) =>
            of(PvsTaskActions.requestSetTaskCancelError({ error })),
          ),
        ),
      ),
    ),
  );

  readonly afterSetStateSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PvsTaskActions.requestSetTaskAcceptSuccess,
          PvsTaskActions.requestSetTaskOnWaySuccess,
          PvsTaskActions.requestSetTaskInProgressSuccess,
          PvsTaskActions.requestSetTaskAtObjectSuccess,
          PvsTaskActions.requestSetTaskCompleteSuccess,
          PvsTaskActions.requestSetTaskCancelSuccess,
        ),
        tap(() => {
          this.store.dispatch(PvsTaskActions.requestTaskInfo());
        }),
      ),
    { dispatch: false },
  );

  readonly showSucces$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PvsTaskActions.requestSetTaskAcceptSuccess,
          PvsTaskActions.requestSetTaskOnWaySuccess,
          PvsTaskActions.requestSetTaskInProgressSuccess,
          PvsTaskActions.requestSetTaskAtObjectSuccess,
          PvsTaskActions.requestSetTaskCompleteSuccess,
          PvsTaskActions.requestSetTaskCancelSuccess,
        ),
        tap((action) => {
          let successTitle: string = null;

          switch (action.type) {
            case '[PVS_TASK] requestSetTaskAcceptSuccess':
              successTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.ACCEPT.SUCCESS',
              );
              break;
            case '[PVS_TASK] requestSetTaskOnWaySuccess':
              successTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.ON_WAY.SUCCESS',
              );
              break;
            case '[PVS_TASK] requestSetTaskInProgressSuccess':
              successTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.IN_PROGRESS.SUCCESS',
              );
              break;
            case '[PVS_TASK] requestSetTaskAtObjectSuccess':
              successTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.AT_OBJECT.SUCCESS',
              );
              break;
            case '[PVS_TASK] requestSetTaskCompleteSuccess':
              successTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.COMPLETE.SUCCESS',
              );
              break;
            case '[PVS_TASK] requestSetTaskCancelSuccess':
              successTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.CANCEL.SUCCESS',
              );
              break;
            default:
              successTitle = this.translateService.instant(
                'GENERAL.SUCCESS_REQUEST',
              );
          }

          this.toastService.add({ level: 'success', summary: successTitle });
        }),
      ),
    { dispatch: false },
  );

  readonly showErrors$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          PvsTaskActions.requestSetTaskAcceptError,
          PvsTaskActions.requestSetTaskOnWayError,
          PvsTaskActions.requestSetTaskInProgressError,
          PvsTaskActions.requestSetTaskAtObjectError,
          PvsTaskActions.requestSetTaskCompleteError,
          PvsTaskActions.requestSetTaskCancelError,
        ),
        tap((action) => {
          const error = action.error?.error;
          let errorText: string = error?.message ?? error.error ?? '';
          error?.details
            ?.filter((detail) => !!detail.message)
            ?.forEach((detail) => {
              errorText += `
              ${detail.message}`;
            });
          let errorTitle: string = null;

          switch (action.type) {
            case '[PVS_TASK] requestSetTaskAcceptError':
              errorTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.ACCEPT.ERROR',
              );
              break;
            case '[PVS_TASK] requestSetTaskOnWayError':
              errorTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.ON_WAY.ERROR',
              );
              break;
            case '[PVS_TASK] requestSetTaskInProgressError':
              errorTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.IN_PROGRESS.ERROR',
              );
              break;
            case '[PVS_TASK] requestSetTaskAtObjectError':
              errorTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.AT_OBJECT.ERROR',
              );
              break;
            case '[PVS_TASK] requestSetTaskCompleteError':
              errorTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.COMPLETE.ERROR',
              );
              break;
            case '[PVS_TASK] requestSetTaskCancelError':
              errorTitle = this.translateService.instant(
                'PVS_TASK.FUNCTIONS.CANCEL.ERROR',
              );
              break;
            default:
              errorTitle = this.translateService.instant(
                'GENERAL.ERROR_REQUEST',
              );
              break;
          }

          this.toastService.add({
            level: 'error',
            summary: errorTitle,
            detail: errorText,
          });
        }),
      ),
    { dispatch: false },
  );
}
